<template>
    <section class="searchbox-container">
        <!-- FORMS -->
        <div class="forms">
            <template v-for="tab in blok.Tab" :key="tab._uid">
                <component
                    :is="FormComponents[tab.Type]"
                    :tabs="tab"
                    :loading="loading"
                    :show-promo="isPromoVisible"
                    :default-airport="blok.DefaultAirport"
                    :default-promo="defaultPromo"
                    :airport-list="airportsList"
                    :style="formStyles"
                    @submit-search="submitForm"
                    @toggle-loading="toggleLoading"
                />
            </template>
        </div>
    </section>
</template>

<script setup lang="ts">
import HotelWithParkingComponent from './SimpleFormLegacy/HotelWithParking.vue';
import ParkingComponent from './SimpleFormLegacy/Parking.vue';
import LoungeComponent from './SimpleFormLegacy/Lounge.vue';
import { useSearchStore } from '@nuxt-monorepo/base/app/store/search';

import type { ISearchTrip } from '@nuxt-monorepo/ui/types/components/Searchbox';

// Calendar Styles
import '@angelblanco/v-calendar/style.css';

const { blok, loading } = defineProps<{ blok: any; loading: boolean }>();
const emit = defineEmits<{
    (e: 'submitStoryblok', body: any): void;
    (e: 'toggleLoading'): void;
}>();

const formStyles = computed(() => ({
    '--preloaded-background-color': blok.preloadedBgColor?.color || null,
}));

const FormComponents: any = {
    HotelWithParking: HotelWithParkingComponent,
    Parking: ParkingComponent,
    Lounge: LoungeComponent,
};

const route: any = useRoute();
const searchStore: any = useSearchStore();

searchStore.updatePromo(route.query.promo || route.query.discount);

if (new Date(searchStore.dates.start) < new Date()) {
    searchStore.update({ dates: { start: '', end: '' } });
}

const submitForm = (body: ISearchTrip) => {
    emit('submitStoryblok', body);
};
const toggleLoading = () => {
    emit('toggleLoading');
};
const airportsList = [
    { label: 'Select Airport', code: '', value: 0, disabled: false },
    { label: 'Aberdeen', code: 'ABZ', value: 18, disabled: false },
    // { label: 'Belfast City', code: 'BHD', value: 33, disabled: false },
    { label: 'Belfast International', code: 'BFS', value: 11, disabled: false },
    { label: 'Birmingham', code: 'BHX', value: 12, disabled: false },
    // { label: 'Bournemouth', code: 'BOH', value: 30, disabled: false },
    { label: 'Bristol', code: 'BRS', value: 3, disabled: false },
    { label: 'Cardiff', code: 'CWL', value: 13, disabled: false },
    // { label: 'Doncaster', code: 'DSA', value: 21, disabled: false },
    // { label: 'Dover Port', code: 'DOV', value: 24, disabled: false },
    { label: 'East Midlands', code: 'EMA', value: 4, disabled: false },
    { label: 'Edinburgh', code: 'EDI', value: 5, disabled: false },
    { label: 'Exeter', code: 'EXT', value: 19, disabled: false },
    { label: 'Gatwick', code: 'LGW', value: 16, disabled: false },
    { label: 'Glasgow', code: 'GLA', value: 6, disabled: false },
    { label: 'Heathrow', code: 'LHR', value: 7, disabled: false },
    { label: 'Humberside', code: 'HUY', value: 20, disabled: false },
    // { label: 'Inverness', code: 'INV', value: 26, disabled: false },
    { label: 'Leeds Bradford', code: 'LBA', value: 14, disabled: false },
    { label: 'Liverpool', code: 'LPL', value: 8, disabled: false },
    { label: 'London City', code: 'LCY', value: 23, disabled: false },
    { label: 'Luton', code: 'LTN', value: 2, disabled: false },
    { label: 'Manchester', code: 'MAN', value: 1, disabled: false },
    { label: 'Newcastle', code: 'NCL', value: 15, disabled: false },
    // { label: 'Norwich', code: 'NWI', value: 27, disabled: false },
    { label: 'Prestwick', code: 'PIK', value: 9, disabled: false },
    { label: 'Southampton', code: 'SOU', value: 31, disabled: false },
    { label: 'Southampton Port', code: 'GBSOU', value: 22, disabled: false },
    { label: 'Southend', code: 'SEN', value: 32, disabled: false },
    { label: 'Stansted', code: 'STN', value: 10, disabled: false },
    // { label: 'Teesside International', code: 'MME', value: 28, disabled: false },
];

const isPromoVisible = computed(() => {
    return route.query.promo || route.query.discount || blok.ShowPromo;
});

const defaultPromo = computed(() => {
    return blok.Promo || route.query.promo || route.query.discount;
});
</script>

<style lang="postcss" scoped>
.forms {
    @apply transition-all w-full;
}

.form {
    @apply relative flex flex-col md:flex-row py-4 md:pt-12 lg:pt-4;
    --preloaded-background-color: #f6ab2f;

    :deep(.airport-preloaded) {
        @apply hidden md:flex items-center justify-center md:absolute md:top-4 lg:top-0 lg:relative lg:max-w-[190px] lg:h-[82px] rounded-l pl-4 pr-4 mb-0;
        @apply lg:bg-[color:var(--preloaded-background-color)];
        .icon {
            @apply hidden lg:flex items-center justify-center pr-2;
            img {
                @apply min-w-[54px] max-w-[54px] object-contain;
            }
        }
        .content {
            @apply relative z-[1] flex-1 text-sm text-primary lg:text-white font-semibold uppercase  max-h-[80px] overflow-hidden;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 4;
        }

        &::after {
            @apply lg:content-[''] absolute top-[50%] left-[100%] -mt-[42px] -ml-[1px] border-transparent;
            @apply border-l-[var(--preloaded-background-color)];
            border-width: 42px 0 42px 20px;
            @apply transition-all duration-300 ease-in-out;
        }
    }

    :deep(.airport-preloaded + div .input-group-container .input-group:first-child) {
        @apply border-l-0 md:border-l rounded-l lg:pl-10 lg:border-l-0;
    }

    :deep(.dates-container) {
        @apply w-full;
    }

    :deep(.group-inputs) {
        @apply flex flex-col sm:flex-row;
    }

    :deep(.group-inputs .input-group:first-child) {
        @apply flex-1;
    }

    &:deep(.input-group-container.guests) {
        @apply xl:max-w-[190px] xl:min-w-[190px];
    }

    :deep(.input-group-container) {
        @apply relative flex-1 bg-white rounded md:bg-transparent pr-0;

        .input-group-title {
            @apply static block w-full text-sm md:whitespace-nowrap pointer-events-none;

            &.has-icon {
                @apply left-9;
            }
        }

        .error-tooltip {
            @apply md:absolute left-4 -bottom-6 block -mt-[2px] mx-2 mb-4 md:mx-0 md:mb-0 px-2 py-2 rounded-md bg-red-500 text-white text-xs whitespace-nowrap z-[1];
            &.custom-date {
                @apply inline;
                white-space: break-spaces;
                @media screen and (min-width: 767px) {
                    white-space: nowrap;
                }
            }
            &.dropoff,
            &.nightOfStay {
                @apply left-10;
            }
        }

        .input-group {
            @apply relative md:border-b md:border-t border-primary md:border-l-0 md:border-r-0 lg:border-r lg:border-gray-300 border-l-0 mr-0 pt-2 pb-3 px-2 lg:px-4 md:basis-0;

            .tooltip {
                @apply hidden;
            }

            .input-icon-prepend {
                @apply hidden;
            }

            .input-icon-append {
                @apply absolute top-[29px] right-[9px] lg:right-[17px] max-w-[38px] max-h-[38px] p-[11px] flex items-center justify-center rounded-r-sm bg-secondary text-white pointer-events-none;
                &:not(.hide-desktop) + input {
                    @apply pr-7;
                }

                [data-icon='sort-down'] {
                    @apply pt-1 pb-3;
                }
            }

            input,
            select {
                @apply h-10 w-full md:pt-4 md:pt-0 pl-2 pr-2 bg-white border border-gray-300 rounded text-sm text-gray-600 font-normal hover:border-primary outline-none text-ellipsis;

                &::placeholder {
                    @apply font-thin;
                }

                &[readonly] {
                    @apply cursor-pointer;
                }
            }

            input {
                @apply cursor-pointer hover:border-primary focus:border-primary md:min-w-[120px];
            }

            select {
                @apply cursor-pointer hover:border-primary focus:border-primary md:min-w-[100px] xl:min-w-[150px];
                option[disabled] {
                    @apply text-[#b0b4bb];
                }
            }

            &.error {
                input,
                select {
                    @apply border-2 !border-red-500;
                }
            }

            .dropdown-list,
            .dropdown-select {
                @apply max-w-full min-w-full md:min-w-0 md:max-w-none absolute top-16 mt-2 md:mt-0 border border-gray-400 rounded bg-white shadow-md md:whitespace-nowrap z-[26];
            }

            .dropdown-list {
                &-item {
                    @apply flex py-4 px-4 border-b border-gray-300 cursor-pointer;
                    &.active {
                        @apply bg-gray-500;
                    }
                    &:hover {
                        @apply bg-gray-200;
                    }
                }
            }

            .dropdown-select {
                @apply py-2;
                &-item {
                    @apply flex py-2 px-4;
                    &-selectors {
                        @apply flex pl-4;
                        .button {
                            @apply mx-2 bg-none border-0 cursor-pointer text-primary-500 text-lg;
                            &:hover {
                                @apply text-primary-350;
                            }
                            &.disabled {
                                @apply text-gray-500;
                            }
                        }
                    }
                }
            }
        }
    }

    :deep(.submit) {
        @apply bg-transparent ml-0;
        span {
            @apply inline-block text-sm;
            &.fullname {
                @apply invisible;
            }
        }
        button {
            @apply h-full w-full px-4 py-2 mt-4 md:mt-0 bg-primary hover:bg-secondary md:text-lg font-semibold text-white md:uppercase rounded md:rounded-l-none transition-all duration-300;
            span {
                @apply inline-block text-lg;
                &.fullname {
                    @apply hidden;
                }
            }
            .loading-icon {
                @apply mt-2 w-[80px];
            }
        }
    }
}
</style>
